import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import {
  FiArrowLeft,
  FiUser,
  FiCalendar,
  FiX,
  FiAlertCircle,
  FiPlus,
  FiMinus,
  FiEdit2,
  FiClock,
  FiCreditCard,
  FiArrowRight,
  FiMapPin
} from "react-icons/fi";
import { 
  BsPersonBadge, 
  BsShieldCheck,
  BsEnvelope
} from "react-icons/bs";
import BusLayout from "../BusLayouts/BusLayout";
import BusBoardingPoints from "../BusBoardingPoints/BusBoardingPoints2";
import styles from "./BusSeatSelection2.module.css";

const BusSeatSelection2 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const bus = params?.bus || location.state?.bus;
  const searchQuery = params?.searchQuery || location.state?.searchQuery;

  const [seatLayoutData, setSeatLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerCount, setPassengerCount] = useState(1);
  const [showBoardingPoints, setShowBoardingPoints] = useState(false);
  const [selectedBoardingPoint, setSelectedBoardingPoint] = useState(null);
  const [selectedDroppingPoint, setSelectedDroppingPoint] = useState(null);
  const [passengerDetails, setPassengerDetails] = useState([]);
  const [seatTypes, setSeatTypes] = useState({});
  const [fareSummary, setFareSummary] = useState({ baseFare: 0, convenienceFee: 0, total: 0 });
  const [activeStep, setActiveStep] = useState(1);
  const isUpdating = useRef(false);

  // Helper Formats
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
  };
  const formatCurrency = (amount) => `₹${Math.round(parseFloat(amount) || 0).toLocaleString("en-IN")}`;

  useEffect(() => {
    if (!bus || !searchQuery) {
      if (mode === "builder") onNavigate({ name: "bus-results" });
      else navigate("/template/bus/results");
      return;
    }
    fetchSeatLayout();
  }, [bus, searchQuery]);

  useEffect(() => {
    const initialDetails = Array.from({ length: passengerCount }, (_, index) => ({
      firstName: "", lastName: "", age: "", title: "Mr", idProof: "", idProofNumber: "",
      email: user?.email || "", phone: user?.phoneNumber || "", seatNumber: selectedSeats[index]?.id || "",
    }));
    setPassengerDetails(initialDetails);
  }, [user, passengerCount]);

  useEffect(() => {
    if (seatLayoutData?.seats) {
      const types = {};
      seatLayoutData.seats.forEach((seat) => {
        if (seat.available) {
          const type = seat.sleeper ? (seat.SeatType === 1 ? "UPPER_SLEEPER" : "LOWER_SLEEPER") : "SEATER";
          types[type] = (types[type] || 0) + 1;
        }
      });
      setSeatTypes(types);
    }
  }, [seatLayoutData]);

  useEffect(() => {
    if (isUpdating.current || selectedSeats.length === 0) return;
    isUpdating.current = true;
    if (selectedSeats.length !== passengerCount) {
      setPassengerCount(selectedSeats.length);
      if (selectedSeats.length > passengerDetails.length) {
        const toAdd = selectedSeats.length - passengerDetails.length;
        const newPs = Array.from({ length: toAdd }, (_, i) => ({
          firstName: "", lastName: "", age: "", title: "Mr", idProof: "", idProofNumber: "",
          email: user?.email || "", phone: user?.phoneNumber || "",
          seatNumber: selectedSeats[passengerDetails.length + i]?.id || "",
        }));
        setPassengerDetails(prev => [...prev, ...newPs]);
      } else if (selectedSeats.length < passengerDetails.length) {
        setPassengerDetails(prev => prev.slice(0, selectedSeats.length));
      }
    }
    const updatedDetails = passengerDetails.map((detail, index) => ({
        ...detail,
        seatNumber: selectedSeats[index]?.id || detail.seatNumber,
    }));
    setPassengerDetails(updatedDetails);
    updateFareSummary();
    if (selectedSeats.length > 0) setActiveStep(2);
    setTimeout(() => { isUpdating.current = false; }, 100);
  }, [selectedSeats]);

  const fetchSeatLayout = async () => {
    setLoading(true); setError(null);
    try {
      const requestData = {
        CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        Source: searchQuery.fromStation, Destination: searchQuery.toStation,
        DateOfJourney: searchQuery.travelDate, InventoryType: bus.inventoryType,
        RouteScheduleId: bus.routeScheduleId,
      };
      const response = await apiService.post(`${config.GETBUSSEATS}/${bus.supplierId}`, requestData);
      if (response) setSeatLayoutData(response);
      else setError("Failed to load layout.");
    } catch (err) { setError("Error fetching seats."); }
    finally { setLoading(false); }
  };

  const handleSeatSelect = (seat) => {
    if (!seat.available || seat.bookedBy) return;
    const isSelected = selectedSeats.some(s => s.id === seat.id);
    if (isSelected) {
      const newSeats = selectedSeats.filter(s => s.id !== seat.id);
      setSelectedSeats(newSeats);
      if (newSeats.length === 0) { setSelectedBoardingPoint(null); setSelectedDroppingPoint(null); setActiveStep(1); }
    } else {
      setSelectedSeats([...selectedSeats, seat]);
    }
  };

  const updateFareSummary = () => {
    const base = selectedSeats.reduce((t, s) => t + (parseFloat(s.totalPriceWithTaxes) || 0), 0);
    const fee = selectedSeats.reduce((t, s) => t + (parseFloat(s.convenienceFee) || 100), 0);
    setFareSummary({ baseFare: base, convenienceFee: fee, total: base + fee });
  };

  const handlePassengerCountChange = (count) => {
    if (count < 1 || count > 6) return;
    isUpdating.current = true;
    if (count < passengerCount) {
      setPassengerCount(count);
      if (selectedSeats.length > count) setSelectedSeats(prev => prev.slice(0, count));
      setPassengerDetails(prev => prev.slice(0, count));
    } else {
      setPassengerCount(count);
      const toAdd = count - passengerDetails.length;
      const newPs = Array.from({ length: toAdd }, () => ({
        firstName: "", lastName: "", age: "", title: "Mr", idProof: "", idProofNumber: "",
        email: user?.email || "", phone: user?.phoneNumber || "", seatNumber: "",
      }));
      setPassengerDetails(prev => [...prev, ...newPs]);
    }
    setTimeout(() => { isUpdating.current = false; }, 100);
  };

  const handlePassengerDetailChange = (index, field, value) => {
    const updated = [...passengerDetails];
    updated[index] = { ...updated[index], [field]: value };
    setPassengerDetails(updated);
  };

  const handleEmailChange = (email) => setPassengerDetails(prev => prev.map(d => ({ ...d, email })));
  const handlePhoneChange = (phone) => setPassengerDetails(prev => prev.map(d => ({ ...d, phone })));

  const areAllPassengerDetailsFilled = () => {
    return passengerDetails.every(p => p.firstName && p.lastName && p.age && p.idProof && p.email && p.phone);
  };

  const handleBoardingPointsSubmit = (bp, dp) => {
    setSelectedBoardingPoint(bp); setSelectedDroppingPoint(dp);
    setShowBoardingPoints(false); setActiveStep(3);
  };

  const handleProceedToPayment = () => {
    const paymentData = { bus, searchQuery, selectedSeats, passengerCount, passengerDetails, selectedBoardingPoint, selectedDroppingPoint, totalAmount: fareSummary.total, baseFare: fareSummary.baseFare, convenienceFee: fareSummary.convenienceFee };
    if (mode === "builder") {
      onNavigate({ name: "bus-payment", params: paymentData });
    } else if (mode === "preview") {
      // FIX: Changed bus/payment to bus-payment to prevent React Router crashes
      navigate(`/template-preview/${templateId}/bus-payment`, { state: paymentData });
    } else {
      navigate("/template/bus-payment", { state: paymentData });
    }
  };

  const syncSeatsWithForms = () => {
    if (selectedSeats.length > passengerDetails.length) {
      const formsToAdd = selectedSeats.length - passengerDetails.length;
      const newPs = Array.from({ length: formsToAdd }, (_, i) => ({
        firstName: "", lastName: "", age: "", title: "Mr", idProof: "", idProofNumber: "",
        email: user?.email || "", phone: user?.phoneNumber || "",
        seatNumber: selectedSeats[passengerDetails.length + i]?.id || "",
      }));
      setPassengerDetails(prev => [...prev, ...newPs]);
    } else {
      setPassengerDetails(prev => prev.slice(0, selectedSeats.length));
    }
    setPassengerCount(selectedSeats.length);
  };

  const getStepStatus = (step) => {
    if (step === 1) return selectedSeats.length > 0 ? styles.completed : styles.active;
    if (step === 2) {
      if (selectedSeats.length === 0) return styles.pending;
      return areAllPassengerDetailsFilled() ? styles.completed : styles.active;
    }
    if (step === 3) {
      if (!selectedBoardingPoint) return styles.pending;
      return styles.completed;
    }
    return styles.pending;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContent}>
        <div className={styles.container}>
          {/* Detailed Header Restored */}
          <div className={styles.pageHeader}>
            <div className={styles.headerLeft}>
              <button className={styles.backButton} onClick={() => navigate(-1)}><FiArrowLeft /></button>
              <div><h1>Select Your Seats</h1><p>Choose seats and provide passenger details</p></div>
            </div>
            <div className={styles.journeyInfo}>
              <span>{searchQuery.fromStation}</span>
              <FiArrowRight />
              <span>{searchQuery.toStation}</span>
              <span className={styles.dateBadge}><FiCalendar />{formatDate(searchQuery.travelDate)}</span>
            </div>
          </div>

          <div className={styles.progressSteps}>
            <div className={`${styles.step} ${getStepStatus(1)}`}><div className={styles.stepNumber}>1</div><div className={styles.stepLabel}>Select Seats</div></div>
            <div className={styles.stepConnector}></div>
            <div className={`${styles.step} ${getStepStatus(2)}`}><div className={styles.stepNumber}>2</div><div className={styles.stepLabel}>Passenger Details</div></div>
            <div className={styles.stepConnector}></div>
            <div className={`${styles.step} ${getStepStatus(3)}`}><div className={styles.stepNumber}>3</div><div className={styles.stepLabel}>Boarding Points</div></div>
          </div>

          <div className={styles.contentGrid}>
            <div className={styles.mainContentArea}>
              {/* Bus Info with RTC Restored */}
              <div className={styles.busInfoCard}>
                <div className={styles.busOperator}>
                  <div className={styles.goldLine}></div>
                  <div>
                    <h3>{bus.operatorName}</h3>
                    <div className={styles.busMeta}>
                      <span className={styles.busType}>{bus.busType}</span>
                      {bus.rtc && <span className={styles.rtcTag}>RTC</span>}
                    </div>
                  </div>
                </div>
                <div className={styles.routeInfo}>
                  <div className={styles.timeInfo}><span className={styles.time}>{bus.departureTime}</span><span className={styles.location}>{searchQuery.fromStation}</span></div>
                  <div className={styles.duration}><FiClock /><span>{Math.floor(bus.durationInMins/60)}h {bus.durationInMins%60}m</span></div>
                  <div className={styles.timeInfo}><span className={styles.time}>{bus.arrivalTime}</span><span className={styles.location}>{searchQuery.toStation}</span></div>
                </div>
              </div>

              {/* Layout Restored */}
              <div className={styles.seatLayoutCard}>
                <div className={styles.cardHeader}><div className={styles.goldLine}></div><h3>Seat Layout</h3>
                  <div className={styles.headerActions}>
                    <div className={styles.seatCountBadge}><BsPersonBadge /><span>{selectedSeats.length} Selected</span></div>
                    {selectedSeats.length > 0 && <button className={styles.clearButton} onClick={() => setSelectedSeats([])}><FiX /> Clear All</button>}
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {loading ? <div className={styles.loadingContainer}><div className={styles.spinner}></div><p>Loading Layout...</p></div> : (
                    <>
                      <div className={styles.seatLegend}>
                        <div className={styles.legendItem}><div className={`${styles.legendSeat} ${styles.available}`}></div><span>Available</span></div>
                        <div className={styles.legendItem}><div className={`${styles.legendSeat} ${styles.selected}`}></div><span>Selected</span></div>
                        <div className={styles.legendItem}><div className={`${styles.legendSeat} ${styles.booked}`}></div><span>Booked</span></div>
                        <div className={styles.legendItem}><div className={`${styles.legendSeat} ${styles.ladies}`}></div><span>Ladies</span></div>
                        <div className={styles.legendItem}><div className={`${styles.legendSeat} ${styles.sleeper}`}></div><span>Sleeper</span></div>
                      </div>
                      <div className={styles.layoutContainer}>
                        <BusLayout seatData={seatLayoutData} onSeatSelect={handleSeatSelect} selectedSeats={selectedSeats} />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Sync Logic & Full Titles Restored */}
              {selectedSeats.length > 0 && (
                <div className={styles.passengerCard}>
                  <div className={styles.cardHeader}>
                    <div className={styles.goldLine}></div>
                    <h3>Passenger Details</h3>
                    <div className={styles.passengerControls}>
                        <button className={styles.controlBtn} onClick={() => handlePassengerCountChange(passengerCount - 1)} disabled={passengerCount <= 1}><FiMinus /></button>
                        <span className={styles.count}>{passengerCount}</span>
                        <button className={styles.controlBtn} onClick={() => handlePassengerCountChange(passengerCount + 1)} disabled={passengerCount >= 6}><FiPlus /></button>
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    {selectedSeats.length !== passengerDetails.length && (
                      <div className={styles.warningAlert}><FiAlertCircle /><span>Seats and forms match error</span><button className={styles.syncButton} onClick={syncSeatsWithForms}>Sync Forms</button></div>
                    )}
                    {passengerDetails.map((p, i) => (
                      <div key={i} className={styles.passengerForm}>
                        <div className={styles.formHeader}><span>Passenger {i+1}</span><span className={styles.seatBadge}>Seat {selectedSeats[i]?.id}</span></div>
                        <div className={styles.formGrid}>
                          <div className={styles.formGroup}><label>Title *</label><select value={p.title} onChange={(e) => handlePassengerDetailChange(i, "title", e.target.value)}><option value="Mr">Mr</option><option value="Ms">Ms</option><option value="Mrs">Mrs</option><option value="Master">Master</option></select></div>
                          <div className={styles.formGroup}><label>First Name *</label><input type="text" placeholder="First name" value={p.firstName} onChange={(e) => handlePassengerDetailChange(i, "firstName", e.target.value)} /></div>
                          <div className={styles.formGroup}><label>Last Name *</label><input type="text" placeholder="Last name" value={p.lastName} onChange={(e) => handlePassengerDetailChange(i, "lastName", e.target.value)} /></div>
                          <div className={styles.formGroup}><label>Age *</label><input type="number" value={p.age} onChange={(e) => handlePassengerDetailChange(i, "age", e.target.value)} /></div>
                          <div className={styles.formGroup}><label>ID Proof *</label>
                            <select value={p.idProof} onChange={(e) => handlePassengerDetailChange(i, "idProof", e.target.value)}>
                              <option value="">Select ID Proof</option><option value="AADHAR">Aadhar Card</option><option value="PAN">PAN Card</option><option value="DL">Driving License</option><option value="VOTER">Voter ID</option><option value="PASSPORT">Passport</option>
                            </select>
                          </div>
                          <div className={styles.formGroup}><label>ID Number</label><input type="text" value={p.idProofNumber} onChange={(e) => handlePassengerDetailChange(i, "idProofNumber", e.target.value)} /></div>
                        </div>
                      </div>
                    ))}
                    <div className={styles.contactSection}>
                      <h4><BsEnvelope /> Contact Information</h4>
                      <div className={styles.contactGrid}>
                        <div className={styles.formGroup}><label>Email *</label><input type="email" value={passengerDetails[0]?.email || ""} onChange={(e) => handleEmailChange(e.target.value)} /><span className={styles.hint}>Ticket will be sent here</span></div>
                        <div className={styles.formGroup}><label>Phone *</label><input type="tel" value={passengerDetails[0]?.phone || ""} onChange={(e) => handlePhoneChange(e.target.value)} /><span className={styles.hint}>Updates via SMS</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.sidebar}>
  <div className={styles.summaryCard}>
    <h3>Booking Summary</h3>
    
    {/* Points Section */}
    <div className={styles.summarySection}>
      <div className={styles.sectionHeader}>
        <FiMapPin />
        <span>Points</span>
      </div>
      
      {selectedBoardingPoint ? (
        <div className={styles.selectedPoints}>
          <div className={styles.point}>
            <div className={styles.pointType}>Boarding</div>
            <div className={styles.pointName}>{selectedBoardingPoint.pointName}</div>
            <div className={styles.pointTime}><FiClock />{formatTime(selectedBoardingPoint.time)}</div>
          </div>
          <div className={styles.pointConnector}></div>
          <div className={styles.point}>
            <div className={styles.pointType}>Dropping</div>
            <div className={styles.pointName}>{selectedDroppingPoint?.pointName}</div>
            <div className={styles.pointTime}><FiClock />{formatTime(selectedDroppingPoint?.time)}</div>
          </div>
          <button className={styles.changeBtn} onClick={() => setShowBoardingPoints(true)}>
            <FiEdit2 /> Change Points
          </button>
        </div>
      ) : (
        <div className={styles.noPointsSelected}>
          <p style={{ fontSize: '13px', color: '#7aab92', margin: '0 0 10px 0' }}>
            Boarding & Dropping points not selected
          </p>
        </div>
      )}
    </div>

    {/* Fare Breakdown */}
    <div className={styles.fareBreakdown}>
      <div className={styles.fareItem}>
        <span>Bus Fare</span>
        <span>{formatCurrency(fareSummary.baseFare)}</span>
      </div>
      <div className={styles.fareItem}>
        <span>Conv. Fee</span>
        <span>{formatCurrency(fareSummary.convenienceFee)}</span>
      </div>
      <div className={styles.fareDivider}></div>
      <div className={`${styles.fareItem} ${styles.total}`}>
        <span>Total Amount</span>
        <span>{formatCurrency(fareSummary.total)}</span>
      </div>
    </div>

    {/* Single Action Button at the bottom */}
    <div className={styles.actionArea}>
      {!selectedSeats.length ? (
        <button className={styles.disabledBtn} disabled>Select Seats to Continue</button>
      ) : !areAllPassengerDetailsFilled() ? (
        <button className={styles.disabledBtn} disabled>Fill Passenger Details</button>
      ) : !selectedBoardingPoint ? (
        <button className={styles.primaryBtn} onClick={() => setShowBoardingPoints(true)}>
          Select Boarding Points
        </button>
      ) : (
        <button className={styles.successBtn} onClick={handleProceedToPayment}>
          <FiCreditCard /> Proceed to Pay
        </button>
      )}
    </div>
    
    <div className={styles.security}>
      <BsShieldCheck /> Secure Checkout
    </div>
  </div>
</div>
          </div>
        </div>
      </div>
      {showBoardingPoints && <BusBoardingPoints bus={bus} onClose={() => setShowBoardingPoints(false)} onSubmit={handleBoardingPointsSubmit} autoProceed={false} />}
    </div>
  );
};

export default BusSeatSelection2;